import {
  Box,
  Column,
  Columns,
  EmptyState,
  FlexBox,
  Text,
} from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import type {
  SearchCompanyHit,
  SearchCompanyResult,
  SearchUserHit,
  SearchUserResult,
} from 'lib-shared-types-api';
import { useCallback, useState } from 'react';

import { useSearchSplitLayout } from '../../../utils/useSearchSplitLayout';
import { SearchResultsEmpty } from '../../empty/searchResultsEmpty';
import { SearchResultsFeedback } from '../../feedback/searchResultsFeedback';

import { ContactTypeDropdown } from './components/dropdown/contactTypeDropdown';
import { ContactPreview } from './components/preview/contactPreview';
import { ContactResult } from './components/result/contactResult';

type ContactsResultsProps = {
  result?: {
    user?: SearchUserResult;
    company?: SearchCompanyResult;
  };
};

/**
 * Represents a component that is used to render the contacts tab of the search
 * results
 */
export const ContactsResults = ({
  result,
}: ContactsResultsProps): JSX.Element => {
  const resultsCount = {
    user: result?.user?.nb_hits ?? 0,
    company: result?.company?.nb_hits ?? 0,
  };

  const [contactType, setContactType] = useState<
    (SearchUserResult | SearchCompanyResult)['hits_type']
  >(
    (resultsCount.user && 'user') ||
      (resultsCount.company > 0 ? 'company' : 'user'),
  );

  const [previewedHit, setPreviewedHit] = useState<
    SearchUserHit | SearchCompanyHit | null
  >(null);

  const { leftColumnSize, rightColumnSize } = useSearchSplitLayout({
    isPreviewing: !!previewedHit,
  });

  const handleNavigateToContact = useCallback(() => {
    ampli.searchContactPreviewGoToClicked({ type: contactType });
  }, [contactType]);

  const handleSelectHit = useCallback(
    (hit: SearchUserHit | SearchCompanyHit) => {
      if (hit.id === previewedHit?.id) return;

      setPreviewedHit(hit);
      ampli.searchContactPreviewOpened({ type: contactType });
    },
    [contactType, previewedHit, setPreviewedHit],
  );

  const handlePreviewClose = useCallback(() => {
    setPreviewedHit(null);
    ampli.searchContactPreviewClosed({ type: contactType });
  }, [contactType, setPreviewedHit]);

  const handleChangeContactType = useCallback(
    (type: (SearchUserResult | SearchCompanyResult)['hits_type']) => {
      setContactType(type);
      setPreviewedHit(null);
      ampli.searchContactTypeChanged({ type });
    },
    [setPreviewedHit],
  );

  const noResults = resultsCount[contactType] === 0;
  if (noResults) {
    return <SearchResultsEmpty />;
  }

  return (
    <Columns>
      <Column size={leftColumnSize}>
        <Column.Row slottable borderBottom>
          <Column.Slot placement="start">
            <ContactTypeDropdown
              counts={resultsCount}
              selected={contactType}
              onChange={handleChangeContactType}
            />
          </Column.Slot>
        </Column.Row>

        <ContactResult
          result={contactType === 'user' ? result?.user : result?.company}
          selectedId={previewedHit?.id}
          onSelectHit={handleSelectHit}
        />
      </Column>

      <Column size={rightColumnSize}>
        <Column.Row scrollable noVerticalPadding noHorizontalPadding>
          {previewedHit ? (
            <ContactPreview
              hit={previewedHit}
              onGoToContact={handleNavigateToContact}
              onPreviewClose={handlePreviewClose}
            />
          ) : (
            <Box padding="500 0">
              <EmptyState
                title="Select a contact"
                description={
                  <FlexBox flexDirection="column" gap="300">
                    <Text.Paragraph>
                      Want to see a preview of the contact you&apos;re looking
                      for? <br /> Go ahead and click a contact to see it here.
                    </Text.Paragraph>

                    <SearchResultsFeedback />
                  </FlexBox>
                }
              />
            </Box>
          )}
        </Column.Row>
      </Column>
    </Columns>
  );
};
